import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext.tsx';
import { supabase } from '../lib/supabaseClient.ts';
import { toast } from 'sonner';
import { usePartnerDetails } from '../hooks/usePartnerDetails.ts';
import { defaultBranding } from '../constants/branding.ts';
import { getContrastTextHSL, getMutedForegroundHSL, hexToHSL } from '../utils/colorUtils.ts';
import { BrandingSettings, BrandingContextType } from '../types/branding.ts';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

const BrandingContext = createContext<BrandingContextType>({
  branding: defaultBranding,
  isWhiteLabel: false,
  isLoading: true,
  currentDomain: '',
  baseUrl: '',
  updateBranding: async () => {},
  resetBranding: async () => {},
  generatePreviewUrl: () => '',
  uploadLogo: async () => '',
  deleteLogo: async () => {},
});

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const { user, profile } = useAuth();
  const { data: partnerDetails, isLoading: partnerLoading } = usePartnerDetails();
  const queryClient = useQueryClient();
  const [branding, setBranding] = useState<BrandingSettings>(defaultBranding);
  const [isWhiteLabel, setIsWhiteLabel] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Get current domain and base URL
  const currentDomain = window.location.hostname;
  const baseUrl = window.location.origin;

  // Memoized logoUrl to prevent flickering
  const memoizedLogoUrl = useMemo(() => branding.logoUrl, [branding.logoUrl]);
  
  const memoizedBranding = useMemo(() => ({
    ...branding,
    logoUrl: memoizedLogoUrl,
    baseUrl: branding.baseUrl || baseUrl,
  }), [
    branding.colors.primary,
    branding.colors.secondary,
    branding.colors.accent,
    branding.companyName,
    memoizedLogoUrl,
    branding.faviconUrl,
    branding.emailStatus,
    branding.fromEmail,
    branding.replyToEmail,
    baseUrl,
    currentDomain
  ]);

  // React Query for partner branding
  const { data: partnerBrandingData } = useQuery({
    queryKey: ['partner-branding', profile?.partner_id || user?.id],
    queryFn: async () => {
      if (!user || !profile) return null;

      const isSubAccount = profile.user_type === 'partner_client' && profile.partner_id;
      
      if (isSubAccount) {
        const { data, error } = await supabase
          .from('partners')
          .select('partner_type, branding_settings')
          .eq('user_id', profile.partner_id)
          .single();
          
        if (error) throw error;
        return data;
      }
      
      return null;
    },
    enabled: !!user && !!profile,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // React Query for public branding (unified for all users)
  const { data: publicBrandingData } = useQuery({
    queryKey: ['public-branding', window.location.hostname, !!user],
    queryFn: async () => {
      const excludedDomains = ['app.schedulehero.ai', 'schedulehero-ai.vercel.app'];
      const currentDomain = window.location.hostname;

      if (excludedDomains.includes(currentDomain)) {
        return null;
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/branding-settings`, {
        method: 'GET',
        headers: {
          'Origin': window.location.origin,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) return null;
      
      return await response.json();
    },
    enabled: true, // For all users now
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  const applyBrandingToDOM = useCallback((settings: BrandingSettings, isWhiteLabelMode: boolean = isWhiteLabel) => {
    const root = document.documentElement;
    
    if (isWhiteLabelMode) {
      // Convert hex colors to HSL format for Tailwind CSS variables
      const primaryHSL = hexToHSL(settings.colors.primary);
      const secondaryHSL = hexToHSL(settings.colors.secondary);
      const accentHSL = hexToHSL(settings.colors.accent);
      const backgroundHSL = hexToHSL(settings.colors.background);
      const foregroundHSL = hexToHSL(settings.colors.foreground);
      
      // Override main Tailwind CSS variables with partner branding
      root.style.setProperty('--primary', primaryHSL);
      root.style.setProperty('--primary-foreground', getContrastTextHSL(settings.colors.primary));
      root.style.setProperty('--secondary', secondaryHSL);
      root.style.setProperty('--secondary-foreground', getContrastTextHSL(settings.colors.secondary));
      root.style.setProperty('--accent', accentHSL);
      root.style.setProperty('--accent-foreground', getContrastTextHSL(settings.colors.accent));
      
      // Update background and foreground if needed
      if (settings.colors.background !== '#FFFFFF') {
        root.style.setProperty('--background', backgroundHSL);
      }
      if (settings.colors.foreground !== '#000000') {
        root.style.setProperty('--foreground', foregroundHSL);
      }
      
      // Calculate and set muted-foreground for proper text visibility
      const mutedForegroundHSL = getMutedForegroundHSL(settings.colors.background, settings.colors.foreground);
      root.style.setProperty('--muted-foreground', mutedForegroundHSL);
      
      // Store original Tailwind values for reset (if not already stored)
      if (!root.style.getPropertyValue('--original-primary')) {
        root.style.setProperty('--original-primary', '227 100% 61%'); // Original Hero Blue
        root.style.setProperty('--original-primary-foreground', '0 0% 100%');
        root.style.setProperty('--original-secondary', '216 25% 95%');
        root.style.setProperty('--original-secondary-foreground', '220 22% 41%');
        root.style.setProperty('--original-accent', '216 25% 95%');
        root.style.setProperty('--original-accent-foreground', '220 22% 41%');
        root.style.setProperty('--original-background', '0 0% 100%');
        root.style.setProperty('--original-foreground', '220 22% 41%');
      }
      
      // Add brand-specific CSS variables for text content
      root.style.setProperty('--partner-company-name', `"${settings.companyName}"`);
      root.style.setProperty('--partner-welcome-message', `"${settings.customCopy.welcomeMessage}"`);
      root.style.setProperty('--partner-ai-name', `"${settings.customCopy.aiName}"`);
      root.style.setProperty('--partner-cta-text', `"${settings.customCopy.ctaText}"`);
      root.style.setProperty('--partner-tagline', `"${settings.customCopy.tagline}"`);
      root.style.setProperty('--partner-ai-greeting', `"${settings.customCopy.aiGreeting}"`);
      root.style.setProperty('--partner-success-title', `"${settings.customCopy.successTitle}"`);
      root.style.setProperty('--partner-success-message', `"${settings.customCopy.successMessage}"`);
      root.style.setProperty('--partner-get-started-text', `"${settings.customCopy.getStartedText}"`);
      root.style.setProperty('--partner-welcome-title', `"${settings.customCopy.welcomeTitle}"`);
      root.style.setProperty('--partner-welcome-subtitle', `"${settings.customCopy.welcomeSubtitle}"`);
      root.style.setProperty('--partner-dashboard-button-text', `"${settings.customCopy.dashboardButtonText}"`);
      
      // Add a CSS class to body to indicate partner branding is active
      document.body.classList.add('partner-branded');
    } else {
      // Restore original Tailwind values
      const originalPrimary = root.style.getPropertyValue('--original-primary');
      if (originalPrimary) {
        root.style.setProperty('--primary', originalPrimary);
        root.style.setProperty('--primary-foreground', root.style.getPropertyValue('--original-primary-foreground'));
        root.style.setProperty('--secondary', root.style.getPropertyValue('--original-secondary'));
        root.style.setProperty('--secondary-foreground', root.style.getPropertyValue('--original-secondary-foreground'));
        root.style.setProperty('--accent', root.style.getPropertyValue('--original-accent'));
        root.style.setProperty('--accent-foreground', root.style.getPropertyValue('--original-accent-foreground'));
        root.style.setProperty('--background', root.style.getPropertyValue('--original-background'));
        root.style.setProperty('--foreground', root.style.getPropertyValue('--original-foreground'));
      }
      
      // Remove partner-specific variables
      const partnerVars = [
        '--partner-company-name', '--partner-welcome-message', '--partner-ai-name',
        '--partner-cta-text', '--partner-tagline', '--partner-ai-greeting',
        '--partner-success-title', '--partner-success-message', '--partner-get-started-text',
        '--partner-welcome-title', '--partner-welcome-subtitle', '--partner-dashboard-button-text'
      ];
      
      partnerVars.forEach(varName => {
        root.style.removeProperty(varName);
      });
      // Reset muted-foreground to default
      root.style.removeProperty('--muted-foreground');
      
      document.body.classList.remove('partner-branded');
    }

    // Preload logo to prevent flashing
    if (settings.logoUrl) {
      const img = new Image();
      img.src = settings.logoUrl;
    }
  }, [isWhiteLabel]);

  // Use publicBrandingData from React Query instead of manual fetch
  const handlePublicBranding = useCallback(() => {
    if (publicBrandingData) {
      const publicBranding = {
        ...defaultBranding,
        ...publicBrandingData,
      };

      setBranding(publicBranding);
      setIsWhiteLabel(true);
      applyBrandingToDOM(publicBranding, true); // Apply as white-label for public branding
    } else {
      // Check if we're on excluded domains
      const excludedDomains = ['app.schedulehero.ai', 'schedulehero-ai.vercel.app'];
      const currentDomain = window.location.hostname;
      
      if (excludedDomains.includes(currentDomain)) {
        setIsWhiteLabel(false);
      }
    }
  }, [publicBrandingData, applyBrandingToDOM]);

  const loadBranding = useCallback(async () => {
    if (!user || !profile) return;

    try {
      setIsLoading(true);

      // Check if user is a sub-account
      const isSubAccount = profile.user_type === 'partner_client' && profile.partner_id;
      
      if (isSubAccount) {
        // Use React Query data if available
        if (partnerBrandingData?.partner_type === 'white_label' && partnerBrandingData.branding_settings) {
          setIsWhiteLabel(true);
          const partnerBranding = {
            ...defaultBranding,
            ...partnerBrandingData.branding_settings,
          };
          setBranding(partnerBranding);
          applyBrandingToDOM(partnerBranding, true);
        } else {
          setIsWhiteLabel(false);
        }
      } else {
        // Regular partner or user logic
        if (partnerDetails?.partner_type === 'white_label' && partnerDetails.branding_settings) {
          setIsWhiteLabel(true);
          const partnerBranding = {
            ...defaultBranding,
            ...partnerDetails.branding_settings,
          };
          setBranding(partnerBranding);
          applyBrandingToDOM(partnerBranding, true);
        } else if (partnerDetails?.partner_type === 'co_branded') {
          setIsWhiteLabel(false);
        } else {
          setIsWhiteLabel(false);
        }
      }
    } catch (error) {
      console.error('Error loading branding:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user, profile, partnerDetails, partnerBrandingData, applyBrandingToDOM]);

  useEffect(() => {
    if (user && profile && !partnerLoading) {
      loadBranding();
    } else if (!user) {
      // Use React Query data for public branding
      if (publicBrandingData) {
        const publicBranding = {
          ...defaultBranding,
          ...publicBrandingData,
        };
        setBranding(publicBranding);
        setIsWhiteLabel(true);
        applyBrandingToDOM(publicBranding, true);
      } else {
        // Handle public branding from React Query
        handlePublicBranding();
        setIsLoading(false);
      }
    }
  }, [user, profile, partnerDetails, partnerLoading, partnerBrandingData, publicBrandingData, loadBranding, handlePublicBranding, applyBrandingToDOM]);

  // Effect to handle public branding data changes
  useEffect(() => {
    if (!user && publicBrandingData) {
      handlePublicBranding();
    }
  }, [publicBrandingData, user, handlePublicBranding]);

  // Separate effect to handle React Query data changes
  useEffect(() => {
    if (!user || !profile || partnerLoading) return;

    const isSubAccount = profile.user_type === 'partner_client' && profile.partner_id;
    
    if (isSubAccount && partnerBrandingData) {
      // Handle partner branding data updates
      if (partnerBrandingData.partner_type === 'white_label' && partnerBrandingData.branding_settings) {
        setIsWhiteLabel(true);
        const partnerBranding = {
          ...defaultBranding,
          ...partnerBrandingData.branding_settings,
        };
        setBranding(partnerBranding);
        applyBrandingToDOM(partnerBranding, true);
      } else {
        setIsWhiteLabel(false);
        setBranding(defaultBranding);
        applyBrandingToDOM(defaultBranding, false);
      }
    } else if (!isSubAccount && partnerDetails) {
      // Handle regular partner data updates
      if (partnerDetails.partner_type === 'white_label' && partnerDetails.branding_settings) {
        setIsWhiteLabel(true);
        const partnerBranding = {
          ...defaultBranding,
          ...partnerDetails.branding_settings,
        };
        setBranding(partnerBranding);
        applyBrandingToDOM(partnerBranding, true);
      } else {
        setIsWhiteLabel(false);
        setBranding(defaultBranding);
        applyBrandingToDOM(defaultBranding, false);
      }
    }
  }, [partnerBrandingData, partnerDetails, user, profile, partnerLoading, applyBrandingToDOM]);

  // Apply branding immediately when state changes
  useEffect(() => {
    if (!isLoading && isWhiteLabel) {
      applyBrandingToDOM(branding, isWhiteLabel);
    }
  }, [branding, isWhiteLabel, isLoading, applyBrandingToDOM]);

  const updateBranding = useCallback(async (settings: Partial<BrandingSettings>) => {
    if (!user || !isWhiteLabel) {
      toast.error('Only white-label partners can customize branding');
      return;
    }

    try {
      const newBranding = { ...branding, ...settings };
      const { error } = await supabase.rpc('update_partner_branding', {
        p_user_id: user.id,
        p_branding_settings: newBranding,
      });

      if (error) {
        console.error('Error updating branding:', error);
        toast.error('Failed to update branding settings');
        return;
      }

      // Update local state immediately
      setBranding(newBranding);
      applyBrandingToDOM(newBranding, true);

      // Invalidate React Query cache for server synchronization
      await queryClient.invalidateQueries({
        queryKey: ['partner-branding']
      });
      
      await queryClient.invalidateQueries({
        queryKey: ['partner-details']
      });

      await queryClient.invalidateQueries({
        queryKey: ['public-branding']
      });

      toast.success('Branding settings updated successfully!');
    } catch (error) {
      console.error('Error updating branding:', error);
      toast.error('Failed to update branding settings');
    }
  }, [user, isWhiteLabel, branding, queryClient, applyBrandingToDOM]);

  const resetBranding = useCallback(async () => {
    if (!user || !isWhiteLabel) {
      toast.error('Only white-label partners can customize branding');
      return;
    }

    try {
      const { error } = await supabase.rpc('update_partner_branding', {
        p_user_id: user.id,
        p_branding_settings: defaultBranding,
      });
      if (error) throw error;
      
      // Update local state immediately
      setBranding(defaultBranding);
      applyBrandingToDOM(defaultBranding, true);

      // Invalidate React Query cache
      await queryClient.invalidateQueries({
        queryKey: ['partner-branding']
      });
      
      await queryClient.invalidateQueries({
        queryKey: ['partner-details']
      });

      toast.success('Branding reset to default settings!');
    } catch (error) {
      console.error('Error resetting branding:', error);
      toast.error('Failed to reset branding settings');
    }
  }, [user, isWhiteLabel, queryClient, applyBrandingToDOM]);

  const generatePreviewUrl = useCallback(() => {
    if (!user) return '';
    const params = new URLSearchParams({
      preview: 'true',
      partner_id: user.id,
      colors: JSON.stringify(branding.colors),
      company_name: branding.companyName,
      logo_url: branding.logoUrl || '',
      custom_copy: JSON.stringify(branding.customCopy),
    });
    return `${window.location.origin}/preview?${params.toString()}`;
  }, [user, branding]);

  const uploadLogo = useCallback(async (file: File, type: 'logo' | 'favicon'): Promise<string> => {
    if (!user || !isWhiteLabel) {
      toast.error('Only white-label partners can upload logos');
      throw new Error('Unauthorized');
    }

    try {
      const formData = new FormData();
      formData.append('logo', file);
      formData.append('type', type);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/partner-branding?action=upload-logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload logo');
      }

      const data = await response.json();
      const logoUrl = data[type + 'Url'];

      // Update branding with new logo URL
      const updatedBranding = {
        ...branding,
        [type + 'Url']: logoUrl,
      };
      
      // Save to database first
      await updateBranding(updatedBranding);
      
      setBranding(updatedBranding);
      applyBrandingToDOM(updatedBranding, true);
      
      toast.success(`${type === 'logo' ? 'Logo' : 'Favicon'} uploaded successfully`);
      return logoUrl;
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error(`Failed to upload ${type === 'logo' ? 'logo' : 'favicon'}`);
      throw error;
    }
  }, [user, isWhiteLabel, branding, applyBrandingToDOM]);

  const deleteLogo = useCallback(async (type: 'logo' | 'favicon'): Promise<void> => {
    if (!user || !isWhiteLabel) {
      toast.error('Only white-label partners can delete logos');
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/partner-branding?action=delete-logo&type=${type}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete logo');
      }

      // Update branding by removing logo URL
      const updatedBranding = {
        ...branding,
        [type + 'Url']: undefined,
      };
      
      setBranding(updatedBranding);
      applyBrandingToDOM(updatedBranding, true);
      
      toast.success(`${type === 'logo' ? 'Logo' : 'Favicon'} deleted successfully`);
    } catch (error) {
      console.error('Error deleting logo:', error);
      toast.error(`Failed to delete ${type === 'logo' ? 'logo' : 'favicon'}`);
    }
  }, [user, isWhiteLabel, branding, applyBrandingToDOM]);

  return (
    <BrandingContext.Provider
      value={{
        branding: memoizedBranding,
        isWhiteLabel,
        isLoading,
        currentDomain,
        baseUrl,
        updateBranding,
        resetBranding,
        generatePreviewUrl,
        uploadLogo,
        deleteLogo,
      }}
    >
      {children}
    </BrandingContext.Provider>
  );
}

export const useBranding = (): BrandingContextType => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};